/**
 * Plan Builder - Constructs Race Plans from Training Plans
 *
 * This module transforms a generic TrainingPlan into a RacePlan that is
 * mapped to specific calendar dates. It handles:
 * - Calculating plan start/end dates based on race date
 * - Mapping workouts to calendar days
 * - Swapping workouts between dates (for user customization)
 * - Swapping all workouts between days of the week
 */
import { differenceInCalendarDays, eachDayOfInterval } from 'date-fns';
import { calcPlanDates, WeekStartsOn } from './datecalc';
import { DateGrid, RacePlan } from './dategrid';
import {
  DayDetails,
  dayOfWeek,
  PlannedWorkout,
  TrainingPlan,
  Units,
} from 'types/app';

/**
 * Converts a PlannedWorkout to DayDetails for display
 */
function renderDayDetails(
  sourceUnits: Units,
  plannedWorkout: PlannedWorkout | undefined,
): DayDetails | undefined {
  if (plannedWorkout) {
    const baseDescription = plannedWorkout.description;

    const description = enrichHyroxDescription(
      baseDescription,
      plannedWorkout,
      sourceUnits,
    );

    return {
      title: plannedWorkout.title,
      desc: description,
      tags: plannedWorkout.tags,
      dist: plannedWorkout.distance,
      sourceUnits: sourceUnits,
      type: plannedWorkout.type,
    };
  } else {
    return undefined;
  }
}

function enrichHyroxDescription(
  description: string | undefined,
  workout: PlannedWorkout,
  sourceUnits: Units,
): string {
  const existing = (description ?? '').trim();
  const type = workout.type ?? '';

  const isHyroxType =
    type === 'Running Intervals' ||
    type === 'Station Work' ||
    type === 'Strength Endurance' ||
    type === 'Full Simulation' ||
    type === 'Hyrox Recovery';

  if (!isHyroxType) return existing;

  const detail = hyroxDetailForWorkout(workout, sourceUnits);
  if (!detail) return existing;

  if (!existing) return detail;

  // If the plan already has structured detail, don't overwrite it.
  const alreadyDetailed =
    /\bWU\b|warm\s*-?up|cool\s*-?down|\bCD\b/i.test(existing) ||
    /\b\d+\s*x\s*\d+/i.test(existing) ||
    /\b\d+\s*x\s*\d+\s*(m|km)\b/i.test(existing);

  if (alreadyDetailed) return existing;

  return `${existing} ${detail}`.trim();
}

function hyroxDetailForWorkout(
  workout: PlannedWorkout,
  sourceUnits: Units,
): string {
  const type = workout.type ?? '';

  // Best-effort distance (assumes Hyrox plans are authored in km).
  const distanceKm =
    typeof workout.distance === 'number'
      ? sourceUnits === 'km'
        ? workout.distance
        : undefined
      : Array.isArray(workout.distance) &&
          typeof workout.distance[0] === 'number'
        ? sourceUnits === 'km'
          ? workout.distance[0]
          : undefined
        : undefined;

  if (type === 'Running Intervals') {
    const total = typeof distanceKm === 'number' ? Math.max(0, distanceKm) : 6;
    const wu = total >= 6 ? 1.5 : 1;
    const cd = total >= 6 ? 1.5 : 1;
    const workKm = Math.max(1, total - wu - cd);

    // Choose a simple, readable main set.
    let repMeters = 800;
    if (workKm <= 2.2) repMeters = 400;
    else if (workKm <= 3.2) repMeters = 600;
    else if (workKm <= 4.2) repMeters = 800;
    else repMeters = 1000;

    const repKm = repMeters / 1000;
    const reps = Math.max(4, Math.min(10, Math.round(workKm / repKm)));

    return `WU ${formatKm(wu)} easy • ${reps}×${repMeters}m hard (5K–10K) w/200m jog • CD ${formatKm(cd)} easy`;
  }

  if (type === 'Station Work') {
    return '2–3 rds: sled push/pull + BBJ + ski/row + carry + wall balls • smooth transitions';
  }

  if (type === 'Strength Endurance') {
    return '3–5 rds steady: squats/lunges + push/pull + core + carries • minimal rest';
  }

  if (type === 'Full Simulation') {
    return '4–6×(1km steady + 1 station) • focus pacing + transitions';
  }

  if (type === 'Hyrox Recovery') {
    return '20–40 min easy + mobility • keep it very easy';
  }

  return '';
}

function formatKm(km: number): string {
  const rounded = Math.round(km * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}km` : `${rounded.toFixed(1)}km`;
}

/**
 * Flattens all workouts from a training plan's weekly schedule
 * into a single ordered array
 */
function getWorkouts(trainingPlan: TrainingPlan): PlannedWorkout[] {
  const result = new Array<PlannedWorkout>();
  for (let w = 0; w < trainingPlan.schedule.length; w++) {
    const currWeek = trainingPlan.schedule[w];
    for (let d = 0; d < currWeek.workouts.length; d++) {
      result.push(currWeek.workouts[d]);
    }
  }
  return result;
}

/**
 * Builds a RacePlan by mapping a TrainingPlan's workouts to calendar dates
 *
 * @param trainingPlan - The source training plan with weekly workouts
 * @param raceDate - The target race/end date
 * @param weekStartsOn - Which day the week starts on (affects calendar display)
 * @returns A RacePlan with workouts mapped to specific dates
 */
export function build(
  trainingPlan: TrainingPlan,
  raceDate: Date,
  weekStartsOn: WeekStartsOn,
): RacePlan {
  // Calculate the date range for the plan
  const planDates = calcPlanDates(
    trainingPlan.schedule.length,
    raceDate,
    weekStartsOn,
  );

  // Get all workouts in order
  const workoutsToPlace = getWorkouts(trainingPlan);
  const totalDaysToPlace =
    differenceInCalendarDays(planDates.planEndDate, planDates.planStartDate) +
    1;

  if (workoutsToPlace.length > totalDaysToPlace) {
    let excess = workoutsToPlace.length - totalDaysToPlace;
    const finalWeekStartIndex = Math.max(workoutsToPlace.length - 7, 0);

    while (
      excess > 0 &&
      finalWeekStartIndex < workoutsToPlace.length - 1 /* keep race day */
    ) {
      workoutsToPlace.splice(finalWeekStartIndex, 1);
      excess--;
    }

    if (excess > 0) {
      // Fallback: drop from the start but still leave the final workout intact
      workoutsToPlace.splice(0, excess);
    }
  }

  // Map each day to its workout
  const map = new Map<Date, DayDetails>();
  eachDayOfInterval({
    start: planDates.planStartDate,
    end: planDates.planEndDate,
  }).forEach((currDate) => {
    const dayDetails = renderDayDetails(
      trainingPlan.units,
      workoutsToPlace.shift(),
    );
    if (dayDetails) {
      map.set(currDate, dayDetails);
    }
  });

  // Create the date grid and return the complete race plan
  const dateGrid = new DateGrid(map, weekStartsOn);
  return {
    raceType: trainingPlan.type,
    planDates: planDates,
    dateGrid: dateGrid,
    sourceUnits: trainingPlan.units,
    description: trainingPlan.description,
    sourceUrl: trainingPlan.source,
  };
}

/**
 * Swaps workouts between two specific dates
 * Returns a new RacePlan (immutable operation)
 */
export function swap(racePlan: RacePlan, d1: Date, d2: Date): RacePlan {
  const newPlan = {
    planDates: racePlan.planDates,
    raceType: racePlan.raceType,
    title: racePlan.raceType,
    dateGrid: racePlan.dateGrid.clone(),
    sourceUnits: racePlan.sourceUnits,
    description: racePlan.description,
    sourceUrl: racePlan.sourceUrl,
  };
  newPlan.dateGrid.swap(d1, d2);
  return newPlan;
}

/**
 * Swaps all workouts between two days of the week
 * (e.g., swap all Tuesdays with all Thursdays)
 * Returns a new RacePlan (immutable operation)
 */
export function swapDow(
  racePlan: RacePlan,
  dow1: dayOfWeek,
  dow2: dayOfWeek,
): RacePlan {
  const newPlan = {
    planDates: racePlan.planDates,
    raceType: racePlan.raceType,
    title: racePlan.raceType,
    dateGrid: racePlan.dateGrid.clone(),
    sourceUnits: racePlan.sourceUnits,
    description: racePlan.description,
    sourceUrl: racePlan.sourceUrl,
  };
  newPlan.dateGrid.swapDow(dow1, dow2);
  return newPlan;
}
